"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Vault = void 0;
const tslib_1 = require("tslib");
const merkle_distributor_1 = require("@saberhq/merkle-distributor");
const solana_contrib_1 = require("@saberhq/solana-contrib");
const token_utils_1 = require("@saberhq/token-utils");
const spl_token_1 = require("@solana/spl-token");
const web3_js_1 = require("@solana/web3.js");
const constants_1 = require("../../constants");
const pda_1 = require("../mine/pda");
const mintWrapper_1 = require("../mintWrapper");
class Vault {
    constructor(sunny, pool, vault) {
        this.sunny = sunny;
        this.pool = pool;
        this.vault = vault;
    }
    get provider() {
        return this.sunny.provider;
    }
    get farmProgram() {
        return this.sunny.programs.Farm;
    }
    get mineProgram() {
        return this.sunny.programs.Mine;
    }
    get program() {
        return this.sunny.programs.SunnySaberFarm;
    }
    /**
     * Deposit LP tokens into a vault.
     * @param amount
     * @returns
     */
    deposit(amount) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const owner = this.provider.wallet.publicKey;
            const { address: ata, instruction } = yield (0, token_utils_1.getOrCreateATA)({
                provider: this.provider,
                mint: this.pool.data.lpMint,
                owner,
            });
            if (instruction) {
                throw new Error("Balance is zero");
            }
            const stake = yield this.getSunnyVaultStakeAccounts();
            return new solana_contrib_1.TransactionEnvelope(this.sunny.provider, [
                spl_token_1.Token.createTransferInstruction(spl_token_1.TOKEN_PROGRAM_ID, ata, this.vault.data.lpTokenAccount, owner, [], amount.toU64()),
                this.program.instruction.deposit(amount.toU64(), { accounts: stake }),
            ]);
        });
    }
    /**
     * Withdraw LP tokens from a vault.
     * @param amount
     * @returns
     */
    withdraw(amount) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const stake = yield this.getSunnyVaultStakeAccounts();
            const owner = this.provider.wallet.publicKey;
            const lpATA = yield (0, token_utils_1.getATAAddress)({
                mint: this.pool.data.lpMint,
                owner,
            });
            const withdrawUnstakedLPIx = this.program.instruction.withdrawUnstakedLp({
                accounts: {
                    owner,
                    pool: this.pool.key,
                    vault: this.vault.key,
                    vaultLpAccount: this.vault.data.lpTokenAccount,
                    lpDestination: lpATA,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                },
            });
            return new solana_contrib_1.TransactionEnvelope(this.sunny.provider, [
                this.program.instruction.withdraw(amount.toU64(), {
                    accounts: stake,
                }),
                withdrawUnstakedLPIx,
            ]);
        });
    }
    /**
     * Claims mining rewards for the owner.
     * @param amount
     * @returns
     */
    claimMineRewards({ owner = this.provider.wallet.publicKey, } = {}) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const rewarderKey = this.sunny.sdk.environment.rewarder;
            const rewarder = yield this.sunny.programs.Mine.account.rewarder.fetch(rewarderKey);
            const [minter] = yield (0, mintWrapper_1.findMinterAddress)(rewarder.mintWrapper, rewarderKey, this.sunny.programs.MintWrapper.programId);
            const mineRewardATA = yield (0, token_utils_1.getOrCreateATA)({
                provider: this.provider,
                mint: rewarder.rewardsTokenMint,
                owner,
            });
            const claimMineRewards = {
                mintWrapper: rewarder.mintWrapper,
                mintWrapperProgram: this.sunny.sdk.programs.MintWrapper.programId,
                minter,
                rewardsTokenMint: rewarder.rewardsTokenMint,
                mineRewardsAccount: mineRewardATA.address,
                stake: yield this.getSunnyVaultStakeAccounts(),
            };
            return new solana_contrib_1.TransactionEnvelope(this.sunny.provider, [
                ...(mineRewardATA.instruction ? [mineRewardATA.instruction] : []),
                this.program.instruction.claimMineRewards({
                    accounts: claimMineRewards,
                }),
            ]);
        });
    }
    /**
     * Claims SBR rewards for the owner.
     * @param amount
     * @returns
     */
    claimFarmRewards({ owner = this.provider.wallet.publicKey, } = {}) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const mintProxy = this.sunny.programs.MintProxy;
            const mpxState = yield mintProxy.state.fetch();
            const sunnyClaimRewards = {
                mintProxyState: mintProxy.state.address(),
                proxyMintAuthority: mpxState.proxyMintAuthority,
                mintProxyProgram: mintProxy.programId,
                minterInfo: yield mintProxy.account.minterInfo.associatedAddress(this.pool.data.landlord),
                rewardsTokenMint: this.sunny.sbrAddress,
                vaultRewardsAccount: this.vault.data.rewardsAccount,
                stake: yield this.getSunnyVaultStakeAccounts(),
            };
            const saberATA = yield (0, token_utils_1.getATAAddress)({
                mint: this.sunny.sbrAddress,
                owner,
            });
            const withdrawClaimedSbrIx = this.program.instruction.withdrawClaimedRewards({
                accounts: {
                    owner,
                    pool: this.pool.key,
                    vault: this.vault.key,
                    vaultRewardsAccount: this.vault.data.rewardsAccount,
                    rewardsDestination: saberATA,
                    rewardsFeeDestination: this.pool.data.fees.rewardsAccount,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                },
            });
            return new solana_contrib_1.TransactionEnvelope(this.sunny.provider, [
                this.program.instruction.claimFarmRewards({
                    accounts: sunnyClaimRewards,
                }),
                withdrawClaimedSbrIx,
            ]);
        });
    }
    getSunnyVaultStakeAccounts() {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const plot = yield this.farmProgram.account.plot.associatedAddress(this.pool.data.landlord, this.pool.data.lpMint);
            const farmer = yield this.farmProgram.account.farmer.associatedAddress(this.vault.key, plot);
            const farmerData = yield this.farmProgram.account.farmer
                .fetch(farmer)
                .catch((err) => {
                if (err instanceof Error &&
                    err.message.includes("Account does not exist")) {
                    throw new Error(`Farmer not found: ${farmer.toString()}`);
                }
                throw err;
            });
            const { farmMint } = this.pool.data;
            const vaultFarmTokenAccount = yield (0, token_utils_1.getATAAddress)({
                mint: farmMint,
                owner: this.vault.key,
            });
            const rewarder = this.sunny.sdk.environment.rewarder;
            const [quarry] = yield (0, pda_1.findQuarryAddress)(rewarder, farmMint);
            const [miner] = yield (0, pda_1.findMinerAddress)(quarry, this.vault.key);
            const minerVault = yield (0, token_utils_1.getATAAddress)({
                mint: farmMint,
                owner: miner,
            });
            return {
                owner: this.vault.data.owner,
                pool: this.vault.data.pool,
                vault: this.vault.key,
                tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                clock: web3_js_1.SYSVAR_CLOCK_PUBKEY,
                farm: {
                    landlord: this.pool.data.landlord,
                    vaultLpTokenAccount: this.vault.data.lpTokenAccount,
                    farmProgram: this.sunny.programs.Farm.programId,
                    farmer,
                    plot,
                    farmerVault: farmerData.tokenVaultKey,
                },
                mine: {
                    vaultFarmTokenAccount,
                    mineProgram: this.mineProgram.programId,
                    farmMint,
                    rewarder,
                    quarry,
                    miner,
                    minerVault,
                },
            };
        });
    }
    /**
     * Claims the saber airdrop for the owner.
     * If successful, the user will have an IOU
     * @param amount
     * @returns
     */
    claimSaberAirdrop(index, amount, proof, distributorKey, iouTokenMint, ownerIouATAAddress, vaultIouATA, poolIouATAAddress) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const instructions = [];
            const [claimStatus, claimStatusKeyBump] = yield (0, merkle_distributor_1.findClaimStatusKey)(new token_utils_1.u64(index), distributorKey);
            if (!this.vault.data.owner.equals(this.provider.wallet.publicKey)) {
                throw new Error("Provider public key not the same as owner");
            }
            const accounts = {
                owner: this.vault.data.owner,
                ownerIouTokenAccount: ownerIouATAAddress,
                pool: this.pool.key,
                vault: this.vault.key,
                vaultIouTokenAccount: vaultIouATA,
                rewardsFeeIouTokenAccount: poolIouATAAddress,
                rewardsFeeSbrTokenAccount: this.pool.data.fees.rewardsAccount,
                distributor: distributorKey,
                distributorClaimStatus: claimStatus,
                distributorFrom: yield (0, token_utils_1.getATAAddress)({
                    mint: iouTokenMint,
                    owner: distributorKey,
                }),
                payer: this.provider.wallet.publicKey,
                distributorProgram: constants_1.SUNNY_ADDRESSES.MerkleDistributorStub,
                systemProgram: web3_js_1.SystemProgram.programId,
                tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
            };
            const claimSaberAirdropInstruction = this.program.instruction.claimSaberAirdrop(claimStatusKeyBump, new token_utils_1.u64(index), new token_utils_1.u64(amount), proof, {
                accounts,
            });
            instructions.push(claimSaberAirdropInstruction);
            return new solana_contrib_1.TransactionEnvelope(this.sunny.provider, [
                claimSaberAirdropInstruction,
            ]);
        });
    }
}
exports.Vault = Vault;
//# sourceMappingURL=vault.js.map